<script setup>
import { ref, computed, onMounted } from 'vue';
import { Head, Link, usePage } from '@inertiajs/inertia-vue3';
import BreezeAuthenticatedLayout from '@/Layouts/Authenticated.vue';
import PremiumPageHeader from '@/Components/PremiumPageHeader.vue';
import SectionMain from '@/Components/SectionMain.vue';
import CardBox from '@/Components/CardBox.vue';
import BaseIcon from '@/Components/BaseIcon.vue';
import {
    mdiShieldKey,
    mdiLock,
    mdiLockOpenVariant,
    mdiFileDocumentMultiple,
    mdiFileCheck,
    mdiBookOpenPageVariant,
    mdiArrowLeft,
    mdiCheckCircle,
    mdiAlertCircle,
    mdiCalendarClock,
    mdiAccountEdit,
    mdiInformationOutline,
    mdiCloseCircle,
    mdiClose,
    mdiCog
} from '@mdi/js';

const props = defineProps({
    constantes: Array,
    initialTab: {
        type: String,
        default: 'cierre'
    },
    childLevels: {
        type: Array,
        default: () => []
    },
    criteriaTranslations: {
        type: Array,
        default: () => []
    }
});

const page = usePage();
const confGlobal = computed(() => page.props.value.confGlobal || {});

const activeTab = ref(props.initialTab || 'cierre');
const pinInput = ref('');
const isUnlocked = ref(false);
const pinError = ref('');
const savingKeys = ref({});
const successMsg = ref('');
const errorMsg = ref('');

// Lista reactiva de constantes
const localConstants = ref([]);

onMounted(() => {
    localConstants.value = (props.constantes || []).map(item => ({ ...item }));
});

const correctPin = computed(() => {
    return confGlobal.value.CONSTANT_PIN_SECURITY_MODIFY || '130116';
});

const verifyPin = () => {
    if (pinInput.value === correctPin.value) {
        isUnlocked.value = true;
        pinError.value = '';
    } else {
        pinError.value = 'El PIN ingresado es incorrecto. Intente nuevamente.';
    }
};

const getConstant = (name) => {
    return localConstants.value.find(c => c.name === name);
};

const getConstantValue = (name) => {
    const c = getConstant(name);
    return c ? String(c.value) : '0';
};

// Criterio de publicación
const publishCriteria = computed(() => {
    const c = getConstant('CALIFICATION_PUBLISH_CRITERIA');
    return c ? c.value : 'examenes';
});

const changePublishCriteria = async (newCriteria) => {
    if (!isUnlocked.value) return;
    await updateConstant('CALIFICATION_PUBLISH_CRITERIA', newCriteria);
};

// Listado de los 9 criterios de evaluación
const allCriteriaList = [
    { key: 'criterio1', label: 'Criterio 1', short: 'C1' },
    { key: 'criterio2', label: 'Criterio 2', short: 'C2' },
    { key: 'criterio3', label: 'Criterio 3', short: 'C3' },
    { key: 'criterio4', label: 'Criterio 4', short: 'C4' },
    { key: 'criterio5', label: 'Criterio 5', short: 'C5' },
    { key: 'criterio6', label: 'Criterio 6', short: 'C6' },
    { key: 'examen_parcial', label: 'Examen Parcial', short: 'EP1' },
    { key: 'examen_parcial2', label: 'Examen Parcial 2', short: 'EP2' },
    { key: 'examen_final', label: 'Examen Final', short: 'EF' },
];

const defaultCriteria = ['examen_parcial', 'examen_parcial2', 'examen_final'];

const getLevelCriteria = (levelKey) => {
    const raw = getConstantValue(`CALIFICATION_PUBLISH_CRITERIA_${levelKey}`);
    if (!raw || raw === '0' || raw === '') {
        return [...defaultCriteria];
    }
    try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) return parsed;
    } catch (e) {
        if (typeof raw === 'string' && raw.includes(',')) {
            return raw.split(',').map(s => s.trim());
        }
    }
    return [...defaultCriteria];
};

// Helpers para traducción de criterios por subnivel
const showCriteriaModal = ref(false);
const selectedLevelForInfo = ref(null);

const getChildLevelsForParent = (parentKey) => {
    if (!props.childLevels || !props.childLevels.length) return [];
    return props.childLevels.filter(lvl => {
        const pLabel = (lvl.parent_label || '').toUpperCase();
        const pRef = (lvl.parent_ref || '').toUpperCase();
        if (parentKey === 'INICIAL') return pLabel === 'INICIAL' || pRef === 'INI';
        if (parentKey === 'PRIMARIA') return pLabel === 'PRIMARIA' || pRef === 'PRIM';
        if (parentKey === 'SECUNDARIA') return pLabel === 'SECUNDARIA' || pRef === 'SEC';
        return false;
    });
};

const getCriterionTranslation = (levelId, criterionKey) => {
    if (!props.criteriaTranslations || !props.criteriaTranslations.length) return null;
    const item = props.criteriaTranslations.find(t => t.level == levelId && t.transkey === criterionKey);
    return item ? item.transvalue : null;
};

const getCriterionTooltip = (parentKey, criterionKey) => {
    const isExamen = ['examen_parcial', 'examen_parcial2', 'examen_final'].includes(criterionKey);
    if (isExamen) {
        const crit = allCriteriaList.find(c => c.key === criterionKey);
        return crit ? crit.label : '';
    }
    const children = getChildLevelsForParent(parentKey);
    if (!children.length) return 'Sin grados registrados';
    return children.map(ch => {
        const trans = getCriterionTranslation(ch.id, criterionKey);
        return `${ch.label}: ${trans || '(Por defecto)'}`;
    }).join('\n');
};

const openCriteriaModal = (niv) => {
    selectedLevelForInfo.value = niv;
    showCriteriaModal.value = true;
};

const isCriterionActive = (levelKey, criterionKey) => {
    const list = getLevelCriteria(levelKey);
    return list.includes(criterionKey);
};

const toggleCriterion = async (levelKey, criterionKey) => {
    if (!isUnlocked.value) {
        errorMsg.value = 'Debe ingresar el PIN de seguridad para modificar los criterios.';
        setTimeout(() => { errorMsg.value = ''; }, 4000);
        return;
    }
    const current = getLevelCriteria(levelKey);
    let updated;
    if (current.includes(criterionKey)) {
        updated = current.filter(k => k !== criterionKey);
    } else {
        updated = [...current, criterionKey];
    }
    await updateConstant(`CALIFICATION_PUBLISH_CRITERIA_${levelKey}`, JSON.stringify(updated));
};

const setLevelCriteriaPreset = async (levelKey, preset) => {
    if (!isUnlocked.value) {
        errorMsg.value = 'Debe ingresar el PIN de seguridad para modificar los criterios.';
        setTimeout(() => { errorMsg.value = ''; }, 4000);
        return;
    }
    let updated = [];
    if (preset === 'examenes') {
        updated = ['examen_parcial', 'examen_parcial2', 'examen_final'];
    } else if (preset === 'todos') {
        updated = allCriteriaList.map(c => c.key);
    } else if (preset === 'ninguno') {
        updated = [];
    }
    await updateConstant(`CALIFICATION_PUBLISH_CRITERIA_${levelKey}`, JSON.stringify(updated));
};

// Comprobar si el cierre correspondiente a un publish está activo
const isCloseActiveForPublish = (publishName) => {
    const closeName = publishName.replace('CALIFICATION_PUBLISH_', 'CALIFICATION_CLOSE_');
    return getConstantValue(closeName) === '1';
};

const updateConstant = async (name, newValue) => {
    if (!isUnlocked.value) {
        errorMsg.value = 'Debe ingresar el PIN de seguridad para modificar configuraciones.';
        return;
    }

    // Validación de negocio
    if (name.startsWith('CALIFICATION_PUBLISH_B') && newValue === '1') {
        if (!isCloseActiveForPublish(name)) {
            errorMsg.value = 'No se puede publicar: primero debe cerrar el periodo correspondiente en Cierre de Calificaciones.';
            setTimeout(() => { errorMsg.value = ''; }, 4000);
            return;
        }
    }

    savingKeys.value[name] = true;
    errorMsg.value = '';
    successMsg.value = '';

    try {
        const response = await axios.post(route('control-panel.update-constant'), {
            name: name,
            value: newValue,
            pin_security: pinInput.value
        });

        if (response.data.success) {
            const updated = response.data.constant;
            const target = localConstants.value.find(c => c.name === name);
            if (target) {
                target.value = updated.value;
                target.updated_at = updated.updated_at;
                target.user_name = updated.user_name;
            } else {
                localConstants.value.push({ ...updated });
            }

            // Si se reabrió un cierre, sincronizar la despublicación localmente si aplica
            if (name.startsWith('CALIFICATION_CLOSE_B') && newValue === '0') {
                const pubName = name.replace('CALIFICATION_CLOSE_', 'CALIFICATION_PUBLISH_');
                const pubTarget = localConstants.value.find(c => c.name === pubName);
                if (pubTarget && pubTarget.value === '1') {
                    pubTarget.value = '0';
                    pubTarget.user_name = updated.user_name;
                }
            }

            successMsg.value = response.data.message || 'Actualizado con éxito';
            setTimeout(() => { successMsg.value = ''; }, 3000);
        }
    } catch (err) {
        console.error(err);
        errorMsg.value = err.response?.data?.message || 'Error al actualizar configuración.';
        setTimeout(() => { errorMsg.value = ''; }, 4000);
    } finally {
        savingKeys.value[name] = false;
    }
};

const bimestres = [
    { key: 'B1', label: 'Bimestre 1' },
    { key: 'B2', label: 'Bimestre 2' },
    { key: 'B3', label: 'Bimestre 3' },
    { key: 'B4', label: 'Bimestre 4' },
];

const niveles = [
    { key: 'INICIAL', label: 'Inicial', short: 'INI' },
    { key: 'PRIMARIA', label: 'Primaria', short: 'PRIM' },
    { key: 'SECUNDARIA', label: 'Secundaria', short: 'SEC' },
];
</script>

<template>
    <Head title="Panel de Control Académico" />

    <BreezeAuthenticatedLayout>
        <template #header>
            <PremiumPageHeader
                title="Panel de Control Académico"
                subtitle="Gestión de Cierres de Notas, Publicación de Calificaciones y Libretas"
            >
                <Link
                    :href="route('dashboard')"
                    class="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-slate-700 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 transition shadow-sm"
                >
                    <BaseIcon :path="mdiArrowLeft" :size="18" />
                    <span>Volver al Dashboard</span>
                </Link>
            </PremiumPageHeader>
        </template>

        <SectionMain class="!p-0 space-y-6 max-w-7xl mx-auto">
            <!-- Barra de Seguridad PIN -->
            <div class="bg-white rounded-2xl border border-slate-200/80 shadow-sm p-5 transition-all">
                <div class="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div class="flex items-center gap-3">
                        <div
                            :class="isUnlocked ? 'bg-emerald-100 text-emerald-600 ring-4 ring-emerald-50' : 'bg-amber-100 text-amber-600 ring-4 ring-amber-50'"
                            class="w-12 h-12 rounded-xl flex items-center justify-center transition shrink-0"
                        >
                            <BaseIcon :path="isUnlocked ? mdiLockOpenVariant : mdiShieldKey" :size="28" />
                        </div>
                        <div>
                            <h3 class="text-base font-bold text-slate-800">
                                {{ isUnlocked ? 'Modo de Edición Desbloqueado' : 'Seguridad: Ingrese PIN de Control' }}
                            </h3>
                            <p class="text-xs text-slate-500">
                                {{ isUnlocked ? 'Puede modificar el estado de los cierres y publicaciones.' : 'Para modificar los switches y configuraciones debe ingresar el PIN de seguridad.' }}
                            </p>
                        </div>
                    </div>

                    <!-- Input PIN -->
                    <div class="flex items-center gap-2 w-full md:w-auto">
                        <template v-if="!isUnlocked">
                            <input
                                v-model="pinInput"
                                type="text"
                                name="control_panel_pin_input"
                                id="control_panel_pin_input"
                                autocomplete="off"
                                autocorrect="off"
                                autocapitalize="off"
                                spellcheck="false"
                                data-lpignore="true"
                                data-1p-ignore
                                style="-webkit-text-security: disc; text-security: disc;"
                                placeholder="PIN de seguridad"
                                class="px-3.5 py-2 text-sm border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 w-full md:w-44 font-mono tracking-widest"
                                @keyup.enter="verifyPin"
                            />
                            <button
                                type="button"
                                @click="verifyPin"
                                class="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold rounded-xl shadow-sm transition whitespace-nowrap"
                            >
                                Desbloquear
                            </button>
                        </template>
                        <template v-else>
                            <span class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800">
                                <BaseIcon :path="mdiCheckCircle" :size="16" />
                                Edición activa
                            </span>
                            <button
                                type="button"
                                @click="isUnlocked = false; pinInput = ''"
                                class="px-3 py-1.5 text-xs text-slate-600 hover:text-slate-900 border border-slate-200 rounded-xl hover:bg-slate-50"
                            >
                                Bloquear
                            </button>
                        </template>
                    </div>
                </div>

                <div v-if="pinError" class="mt-3 text-xs text-rose-600 flex items-center gap-1">
                    <BaseIcon :path="mdiAlertCircle" :size="15" />
                    <span>{{ pinError }}</span>
                </div>
            </div>

            <!-- Alertas de éxito / error -->
            <div v-if="successMsg" class="p-3 bg-emerald-50 border border-emerald-200 rounded-xl text-emerald-700 text-sm flex items-center gap-2">
                <BaseIcon :path="mdiCheckCircle" :size="18" />
                <span>{{ successMsg }}</span>
            </div>
            <div v-if="errorMsg" class="p-3 bg-rose-50 border border-rose-200 rounded-xl text-rose-700 text-sm flex items-center gap-2">
                <BaseIcon :path="mdiCloseCircle" :size="18" />
                <span>{{ errorMsg }}</span>
            </div>

            <!-- Navegación de Tabs -->
            <div class="flex border-b border-slate-200 bg-white rounded-t-2xl px-4 pt-2 gap-2 overflow-x-auto">
                <button
                    type="button"
                    @click="activeTab = 'cierre'"
                    :class="activeTab === 'cierre' ? 'border-emerald-600 text-emerald-700 bg-emerald-50/50' : 'border-transparent text-slate-600 hover:text-slate-900'"
                    class="flex items-center gap-2 px-4 py-3 border-b-2 font-semibold text-sm rounded-t-xl transition whitespace-nowrap"
                >
                    <BaseIcon :path="mdiFileDocumentMultiple" :size="20" class="text-emerald-600" />
                    <span>1. Cierre de Calificaciones</span>
                </button>
                <button
                    type="button"
                    @click="activeTab = 'publicar'"
                    :class="activeTab === 'publicar' ? 'border-indigo-600 text-indigo-700 bg-indigo-50/50' : 'border-transparent text-slate-600 hover:text-slate-900'"
                    class="flex items-center gap-2 px-4 py-3 border-b-2 font-semibold text-sm rounded-t-xl transition whitespace-nowrap"
                >
                    <BaseIcon :path="mdiFileCheck" :size="20" class="text-indigo-600" />
                    <span>2. Publicar Calificaciones</span>
                </button>
                <button
                    type="button"
                    @click="activeTab = 'libretas'"
                    :class="activeTab === 'libretas' ? 'border-blue-600 text-blue-700 bg-blue-50/50' : 'border-transparent text-slate-600 hover:text-slate-900'"
                    class="flex items-center gap-2 px-4 py-3 border-b-2 font-semibold text-sm rounded-t-xl transition whitespace-nowrap"
                >
                    <BaseIcon :path="mdiBookOpenPageVariant" :size="20" class="text-blue-600" />
                    <span>3. Publicar Libretas</span>
                </button>
            </div>

            <!-- CONTENIDO TAB 1: Cierre de Calificaciones -->
            <div v-show="activeTab === 'cierre'" class="bg-white rounded-b-2xl border border-slate-200/80 p-6 space-y-6 shadow-sm">
                <div class="flex items-center justify-between border-b border-slate-100 pb-4">
                    <div>
                        <h4 class="text-lg font-bold text-slate-800">Cierre de Calificaciones por Bimestre y Nivel</h4>
                        <p class="text-xs text-slate-500">
                            Al cerrar un bimestre y nivel, los profesores ya no podrán realizar modificaciones en el registro de notas.
                        </p>
                    </div>
                </div>

                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div
                        v-for="bim in bimestres"
                        :key="bim.key"
                        class="p-4 rounded-xl border border-slate-200 bg-slate-50/50 hover:bg-slate-50 transition"
                    >
                        <div class="flex items-center justify-between font-bold text-slate-800 text-sm border-b border-slate-200 pb-2 mb-3">
                            <span class="text-emerald-700">{{ bim.label }}</span>
                            <span class="text-xs text-slate-400 font-normal">Cierre de actas</span>
                        </div>

                        <div class="space-y-3">
                            <div
                                v-for="niv in niveles"
                                :key="niv.key"
                                class="flex items-center justify-between p-2.5 bg-white rounded-lg border border-slate-100 shadow-xs"
                            >
                                <div class="min-w-0 pr-2">
                                    <div class="flex items-center gap-2">
                                        <span class="text-xs font-bold px-2 py-0.5 rounded bg-slate-100 text-slate-700">{{ niv.short }}</span>
                                        <span class="text-xs font-medium text-slate-800">{{ niv.label }}</span>
                                    </div>
                                    <div class="text-[10px] text-slate-400 mt-1 flex items-center gap-2">
                                        <span v-if="getConstant(`CALIFICATION_CLOSE_${bim.key}_${niv.key}`)?.updated_at">
                                            Modif: {{ getConstant(`CALIFICATION_CLOSE_${bim.key}_${niv.key}`)?.updated_at }}
                                        </span>
                                        <span v-if="getConstant(`CALIFICATION_CLOSE_${bim.key}_${niv.key}`)?.user_name">
                                            por: {{ getConstant(`CALIFICATION_CLOSE_${bim.key}_${niv.key}`)?.user_name }}
                                        </span>
                                    </div>
                                </div>

                                <!-- Botón Toggle Si/No -->
                                <div class="flex items-center gap-2">
                                    <button
                                        type="button"
                                        :disabled="!isUnlocked || savingKeys[`CALIFICATION_CLOSE_${bim.key}_${niv.key}`]"
                                        @click="updateConstant(
                                            `CALIFICATION_CLOSE_${bim.key}_${niv.key}`,
                                            getConstantValue(`CALIFICATION_CLOSE_${bim.key}_${niv.key}`) === '1' ? '0' : '1'
                                        )"
                                        :class="[
                                            getConstantValue(`CALIFICATION_CLOSE_${bim.key}_${niv.key}`) === '1'
                                                ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                                                : 'bg-slate-200 text-slate-600 hover:bg-slate-300',
                                            !isUnlocked ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer active:scale-95'
                                        ]"
                                        class="px-3 py-1.5 rounded-lg text-xs font-bold transition flex items-center gap-1.5 shadow-xs"
                                    >
                                        <span v-if="savingKeys[`CALIFICATION_CLOSE_${bim.key}_${niv.key}`]" class="animate-spin text-xs">●</span>
                                        <span>{{ getConstantValue(`CALIFICATION_CLOSE_${bim.key}_${niv.key}`) === '1' ? 'Cerrado' : 'Abierto' }}</span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- CONTENIDO TAB 2: Publicar Calificaciones -->
            <div v-show="activeTab === 'publicar'" class="bg-white rounded-b-2xl border border-slate-200/80 p-6 space-y-6 shadow-sm">
                <div class="border-b border-slate-100 pb-4">
                    <h4 class="text-lg font-bold text-slate-800">Publicar Calificaciones a Estudiantes y Apoderados</h4>
                    <p class="text-xs text-slate-500">
                        Permite hacer visibles las notas en el aula y dashboard. <strong class="text-rose-600">Solo se puede publicar si el cierre de ese periodo está cerrado.</strong>
                    </p>
                </div>

                <!-- SECCIÓN: Criterios a Publicar por Nivel Educativo -->
                <div class="bg-slate-50/70 border border-slate-200/80 rounded-2xl p-5 space-y-4">
                    <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-slate-200 pb-3">
                        <div>
                            <div class="flex items-center gap-2">
                                <span class="w-2.5 h-2.5 rounded-full bg-indigo-600 inline-block"></span>
                                <h5 class="text-sm font-bold text-slate-800">Criterios de Evaluación Visibles por Nivel</h5>
                            </div>
                            <p class="text-xs text-slate-500 mt-0.5">
                                Seleccione qué criterios y exámenes estarán habilitados para los alumnos y apoderados de cada nivel educativo.
                            </p>
                        </div>
                        <div class="flex items-center gap-2 text-xs text-slate-500 bg-white px-3 py-1.5 rounded-xl border border-slate-200/80 shrink-0">
                            <BaseIcon :path="mdiInformationOutline" :size="16" class="text-amber-500 shrink-0" />
                            <span>Los nombres por grado se configuran en <strong>Criterios de Evaluación</strong></span>
                        </div>
                    </div>

                    <!-- Cards por Nivel Educativo -->
                    <div class="grid grid-cols-1 lg:grid-cols-3 gap-4">
                        <div
                            v-for="niv in niveles"
                            :key="'crit-' + niv.key"
                            class="bg-white rounded-xl border border-slate-200 p-4 flex flex-col justify-between shadow-xs transition hover:border-indigo-300"
                        >
                            <div>
                                <!-- Header de Tarjeta: Fila 1 Título y Modal Grados -->
                                <div class="flex items-center justify-between gap-2 pb-2">
                                    <div class="flex items-center gap-2 min-w-0">
                                        <span class="text-xs font-bold px-2 py-0.5 rounded bg-indigo-50 text-indigo-700 border border-indigo-100 shrink-0">
                                            {{ niv.short }}
                                        </span>
                                        <span class="text-sm font-bold text-slate-800 truncate">{{ niv.label }}</span>
                                    </div>
                                    <!-- Botón Modal Informativo de Criterios por Grado -->
                                    <button
                                        type="button"
                                        @click="openCriteriaModal(niv)"
                                        class="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-semibold text-indigo-700 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200/70 rounded-lg transition shrink-0 cursor-pointer shadow-2xs"
                                        title="Ver traducciones de criterios por grado (PC y Móvil)"
                                    >
                                        <BaseIcon :path="mdiInformationOutline" :size="14" class="text-indigo-600" />
                                        <span>Ver Grados</span>
                                    </button>
                                </div>

                                <!-- Header de Tarjeta: Fila 2 Acciones Rápidas -->
                                <div class="flex items-center justify-between text-[11px] font-semibold text-slate-500 bg-slate-50/80 px-2.5 py-1 rounded-lg border border-slate-100 mb-3">
                                    <span class="text-[10px] uppercase font-bold text-slate-400 tracking-wider">Acciones:</span>
                                    <div class="flex items-center gap-1.5">
                                        <button
                                            type="button"
                                            :disabled="!isUnlocked || savingKeys[`CALIFICATION_PUBLISH_CRITERIA_${niv.key}`]"
                                            @click="setLevelCriteriaPreset(niv.key, 'examenes')"
                                            class="text-[11px] text-slate-600 hover:text-indigo-600 transition"
                                            :class="!isUnlocked ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'"
                                            title="Publicar solo Exámenes"
                                        >
                                            Solo Exámenes
                                        </button>
                                        <span class="text-slate-300">·</span>
                                        <button
                                            type="button"
                                            :disabled="!isUnlocked || savingKeys[`CALIFICATION_PUBLISH_CRITERIA_${niv.key}`]"
                                            @click="setLevelCriteriaPreset(niv.key, 'todos')"
                                            class="text-[11px] text-slate-600 hover:text-indigo-600 transition"
                                            :class="!isUnlocked ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'"
                                            title="Publicar Todos los Criterios"
                                        >
                                            Todos
                                        </button>
                                        <span class="text-slate-300">·</span>
                                        <button
                                            type="button"
                                            :disabled="!isUnlocked || savingKeys[`CALIFICATION_PUBLISH_CRITERIA_${niv.key}`]"
                                            @click="setLevelCriteriaPreset(niv.key, 'ninguno')"
                                            class="text-[11px] text-rose-500 hover:text-rose-700 transition"
                                            :class="!isUnlocked ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'"
                                            title="Desmarcar Todos"
                                        >
                                            Limpiar
                                        </button>
                                    </div>
                                </div>

                                <!-- Chips / Switches de Criterios -->
                                <div class="grid grid-cols-3 gap-1.5">
                                    <button
                                        v-for="crit in allCriteriaList"
                                        :key="crit.key"
                                        type="button"
                                        :disabled="!isUnlocked || savingKeys[`CALIFICATION_PUBLISH_CRITERIA_${niv.key}`]"
                                        @click="toggleCriterion(niv.key, crit.key)"
                                        :title="getCriterionTooltip(niv.key, crit.key)"
                                        :class="[
                                            isCriterionActive(niv.key, crit.key)
                                                 ? 'bg-indigo-600 text-white shadow-xs border-indigo-600 hover:bg-indigo-700'
                                                : 'bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100 hover:text-slate-900',
                                            !isUnlocked ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer active:scale-95'
                                        ]"
                                        class="px-2 py-2 rounded-lg text-xs font-semibold border flex flex-col items-center justify-center text-center transition"
                                    >
                                        <span class="text-[10px] uppercase tracking-wider opacity-75 font-mono mb-0.5">{{ crit.short }}</span>
                                        <span class="text-[11px] leading-tight line-clamp-1">{{ crit.label }}</span>
                                    </button>
                                </div>
                            </div>

                            <div class="mt-3 pt-2 border-t border-slate-100 flex items-center justify-between text-[11px] text-slate-400">
                                <span>Activos: <strong class="text-indigo-600 font-bold">{{ getLevelCriteria(niv.key).length }}</strong> / 9</span>
                                <span v-if="savingKeys[`CALIFICATION_PUBLISH_CRITERIA_${niv.key}`]" class="text-indigo-600 font-bold flex items-center gap-1">
                                    <span class="animate-spin text-xs">●</span> Guardando...
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- SECCIÓN: Cierres / Publicación por Bimestre -->
                <div class="pt-2">
                    <div class="flex items-center justify-between mb-3">
                        <h5 class="text-sm font-bold text-slate-800">Publicación por Periodo Bimestral</h5>
                        <span class="text-xs text-slate-400">Active la publicación una vez cerrado el periodo</span>
                    </div>

                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div
                        v-for="bim in bimestres"
                        :key="bim.key"
                        class="p-4 rounded-xl border border-slate-200 bg-slate-50/50 hover:bg-slate-50 transition"
                    >
                        <div class="flex items-center justify-between font-bold text-slate-800 text-sm border-b border-slate-200 pb-2 mb-3">
                            <span class="text-indigo-700">{{ bim.label }}</span>
                            <span class="text-xs text-slate-400 font-normal">Visibilidad alumnos</span>
                        </div>

                        <div class="space-y-3">
                            <div
                                v-for="niv in niveles"
                                :key="niv.key"
                                class="flex items-center justify-between p-2.5 bg-white rounded-lg border border-slate-100 shadow-xs"
                            >
                                <div class="min-w-0 pr-2">
                                    <div class="flex items-center gap-2">
                                        <span class="text-xs font-bold px-2 py-0.5 rounded bg-slate-100 text-slate-700">{{ niv.short }}</span>
                                        <span class="text-xs font-medium text-slate-800">{{ niv.label }}</span>

                                        <!-- Indicador de cierre previo -->
                                        <span
                                            v-if="!isCloseActiveForPublish(`CALIFICATION_PUBLISH_${bim.key}_${niv.key}`)"
                                            class="text-[10px] px-2 py-0.5 rounded bg-amber-100 text-amber-700 font-semibold inline-flex items-center gap-1"
                                            title="El Cierre de calificaciones aún está abierto"
                                        >
                                            <BaseIcon :path="mdiAlertCircle" :size="12" />
                                            Cierre abierto
                                        </span>
                                    </div>
                                    <div class="text-[10px] text-slate-400 mt-1 flex items-center gap-2">
                                        <span v-if="getConstant(`CALIFICATION_PUBLISH_${bim.key}_${niv.key}`)?.updated_at">
                                            Modif: {{ getConstant(`CALIFICATION_PUBLISH_${bim.key}_${niv.key}`)?.updated_at }}
                                        </span>
                                        <span v-if="getConstant(`CALIFICATION_PUBLISH_${bim.key}_${niv.key}`)?.user_name">
                                            por: {{ getConstant(`CALIFICATION_PUBLISH_${bim.key}_${niv.key}`)?.user_name }}
                                        </span>
                                    </div>
                                </div>

                                <!-- Botón Toggle Publicar -->
                                <div class="flex items-center gap-2">
                                    <button
                                        type="button"
                                        :disabled="!isUnlocked || !isCloseActiveForPublish(`CALIFICATION_PUBLISH_${bim.key}_${niv.key}`) || savingKeys[`CALIFICATION_PUBLISH_${bim.key}_${niv.key}`]"
                                        @click="updateConstant(
                                            `CALIFICATION_PUBLISH_${bim.key}_${niv.key}`,
                                            getConstantValue(`CALIFICATION_PUBLISH_${bim.key}_${niv.key}`) === '1' ? '0' : '1'
                                        )"
                                        :class="[
                                            getConstantValue(`CALIFICATION_PUBLISH_${bim.key}_${niv.key}`) === '1'
                                                ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                                                : 'bg-slate-200 text-slate-600 hover:bg-slate-300',
                                            (!isUnlocked || !isCloseActiveForPublish(`CALIFICATION_PUBLISH_${bim.key}_${niv.key}`)) ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer active:scale-95'
                                        ]"
                                        class="px-3 py-1.5 rounded-lg text-xs font-bold transition flex items-center gap-1.5 shadow-xs"
                                        :title="!isCloseActiveForPublish(`CALIFICATION_PUBLISH_${bim.key}_${niv.key}`) ? 'Primero debe cerrar el cierre de este bimestre y nivel' : ''"
                                    >
                                        <span v-if="savingKeys[`CALIFICATION_PUBLISH_${bim.key}_${niv.key}`]" class="animate-spin text-xs">●</span>
                                        <span>{{ getConstantValue(`CALIFICATION_PUBLISH_${bim.key}_${niv.key}`) === '1' ? 'Publicado' : 'No Publicado' }}</span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            </div>

            <!-- CONTENIDO TAB 3: Publicar Libretas -->
            <div v-show="activeTab === 'libretas'" class="bg-white rounded-b-2xl border border-slate-200/80 p-6 space-y-6 shadow-sm">
                <div class="border-b border-slate-100 pb-4">
                    <h4 class="text-lg font-bold text-slate-800">Publicar Libretas a Estudiantes</h4>
                    <p class="text-xs text-slate-500">
                        Configuración de fecha de publicación de libretas por nivel educativo.
                    </p>
                </div>

                <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div
                        v-for="niv in niveles"
                        :key="niv.key"
                        class="p-4 rounded-xl border border-slate-200 bg-slate-50/50 hover:bg-slate-50 transition space-y-4"
                    >
                        <div class="flex items-center justify-between font-bold text-slate-800 text-sm border-b border-slate-200 pb-2">
                            <span class="text-blue-700">{{ niv.label }}</span>
                            <span class="text-xs text-slate-400 font-normal">Nivel</span>
                        </div>

                        <div class="space-y-3 bg-white p-3 rounded-lg border border-slate-100">
                            <label class="text-xs font-semibold text-slate-600 block">Fecha de Publicación</label>
                            <input
                                type="date"
                                :disabled="!isUnlocked"
                                :value="getConstantValue(`LIBRETA_SHOW_TO_STUDENT_${niv.key}`)"
                                @change="updateConstant(`LIBRETA_SHOW_TO_STUDENT_${niv.key}`, $event.target.value)"
                                class="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500"
                            />
                            <div class="flex items-center justify-between pt-2">
                                <span class="text-xs text-slate-500">
                                    Estado: <strong :class="getConstantValue(`LIBRETA_SHOW_TO_STUDENT_${niv.key}`) ? 'text-emerald-600' : 'text-slate-400'">
                                        {{ getConstantValue(`LIBRETA_SHOW_TO_STUDENT_${niv.key}`) ? 'Programada / Activa' : 'No configurada' }}
                                    </strong>
                                </span>
                                <button
                                    v-if="getConstantValue(`LIBRETA_SHOW_TO_STUDENT_${niv.key}`)"
                                    type="button"
                                    :disabled="!isUnlocked"
                                    @click="updateConstant(`LIBRETA_SHOW_TO_STUDENT_${niv.key}`, '')"
                                    class="text-xs text-rose-600 hover:underline"
                                >
                                    Limpiar
                                </button>
                            </div>
                        </div>

                        <div class="text-[10px] text-slate-400">
                            <div v-if="getConstant(`LIBRETA_SHOW_TO_STUDENT_${niv.key}`)?.updated_at">
                                Última modif: {{ getConstant(`LIBRETA_SHOW_TO_STUDENT_${niv.key}`)?.updated_at }}
                            </div>
                            <div v-if="getConstant(`LIBRETA_SHOW_TO_STUDENT_${niv.key}`)?.user_name">
                                Usuario: {{ getConstant(`LIBRETA_SHOW_TO_STUDENT_${niv.key}`)?.user_name }}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- MODAL INFORMATIVO: Criterios por Grado (PC y Móvil) -->
            <div
                v-if="showCriteriaModal && selectedLevelForInfo"
                class="fixed inset-0 z-50 overflow-y-auto bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4"
                @click.self="showCriteriaModal = false"
            >
                <div class="bg-white rounded-2xl max-w-3xl w-full shadow-2xl border border-slate-200 overflow-hidden transform transition-all animate-fadeIn">
                    <!-- Header Modal -->
                    <div class="px-6 py-4 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
                        <div class="flex items-center gap-2.5">
                            <div class="w-8 h-8 rounded-lg bg-indigo-100 text-indigo-700 flex items-center justify-center font-bold text-xs">
                                {{ selectedLevelForInfo.short }}
                            </div>
                            <div>
                                <h3 class="text-base font-bold text-slate-800">
                                    Nombres de Criterios - Nivel {{ selectedLevelForInfo.label }}
                                </h3>
                                <p class="text-xs text-slate-500">
                                    Traducción y etiquetas personalizadas configuradas para cada grado
                                </p>
                            </div>
                        </div>
                        <button
                            type="button"
                            @click="showCriteriaModal = false"
                            class="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-200 transition"
                        >
                            <BaseIcon :path="mdiClose" :size="20" />
                        </button>
                    </div>

                    <!-- Contenido Modal -->
                    <div class="p-6 max-h-[70vh] overflow-y-auto space-y-4">
                        <div class="p-3 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-800 flex items-start gap-2">
                            <BaseIcon :path="mdiInformationOutline" :size="16" class="shrink-0 mt-0.5 text-amber-600" />
                            <div>
                                Estos nombres informativos se configuran por cada grado en el menú <strong>Criterios de Evaluación</strong>. En este Panel de Control usted decide si se hacen visibles para los alumnos y apoderados.
                            </div>
                        </div>

                        <div v-if="getChildLevelsForParent(selectedLevelForInfo.key).length === 0" class="text-center py-6 text-slate-400 text-sm">
                            No hay grados registrados para este nivel.
                        </div>

                        <div v-else class="space-y-3">
                            <div
                                v-for="child in getChildLevelsForParent(selectedLevelForInfo.key)"
                                :key="child.id"
                                class="border border-slate-200 rounded-xl p-3.5 bg-white hover:bg-slate-50/50 transition"
                            >
                                <div class="flex items-center justify-between border-b border-slate-100 pb-2 mb-2">
                                    <span class="text-xs font-bold text-slate-800 flex items-center gap-1.5">
                                        <span class="w-2 h-2 rounded-full bg-indigo-500"></span>
                                        {{ child.label }}
                                    </span>
                                    <span class="text-[11px] text-slate-400 font-mono">ID: {{ child.id }}</span>
                                </div>

                                <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2">
                                    <div
                                        v-for="critNum in 6"
                                        :key="critNum"
                                        class="p-2 rounded-lg bg-slate-50 border border-slate-100 text-center"
                                    >
                                        <span class="block text-[10px] uppercase font-mono font-bold text-slate-400">C{{ critNum }}</span>
                                        <span class="block text-xs font-semibold text-slate-700 truncate" :title="getCriterionTranslation(child.id, `criterio${critNum}`) || `Criterio ${critNum}`">
                                            {{ getCriterionTranslation(child.id, `criterio${critNum}`) || `Criterio ${critNum}` }}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Footer Modal -->
                    <div class="px-6 py-3 bg-slate-50 border-t border-slate-200 flex items-center justify-between">
                        <Link
                            :href="route('config-criteriosevaluacion')"
                            class="text-xs font-semibold text-indigo-600 hover:text-indigo-800 hover:underline flex items-center gap-1"
                        >
                            <span>Ir a editar nombres en Criterios de Evaluación</span>
                            <span>&rarr;</span>
                        </Link>
                        <button
                            type="button"
                            @click="showCriteriaModal = false"
                            class="px-4 py-2 bg-slate-200 hover:bg-slate-300 text-slate-700 font-semibold text-xs rounded-xl transition"
                        >
                            Cerrar
                        </button>
                    </div>
                </div>
            </div>
        </SectionMain>
    </BreezeAuthenticatedLayout>
</template>
